import React, { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import DatePickerComponent from "../../../common/components/DatePickerComponent";
import { TRANSFER_TABS } from "../../../common/constants/dashboardModule/transferConstants";
import {
    fetchTransfersText,
    fetchTransfersEmail,
} from "../../../services/dashboardModule/dashboardServices";
import TextTransfer from "./TextTransfer";
import EmailTransfer from "./EmailTransfer";
import TransfersLanguageStackedAreaChart from "./TransfersChart";

// ── Constants ──────────────────────────────────────────────────────
const BLUE = "#378ADD";

// CSV columns. Backend SQL emits English bucket as `EnglishTotal` and
// Spanish bucket as `Spanish` (no "Total" suffix) — both totals.
const EXPORT_COLS = [
    { key: "LastConvDate",        label: "Last Conv Date", isDate: true },
    { key: "EnglishTotal",        label: "English Total"  },
    { key: "EnglishTransferRate", label: "English Transfer Rate" },
    { key: "EnglishNoTransfer",   label: "English No Transfer" },
    { key: "Spanish",             label: "Spanish Total"  },
    { key: "SpanishTransferRate", label: "Spanish Transfer Rate" },
    { key: "SpanishNoTransfer",   label: "Spanish No Transfer" },
    { key: "TotalRecords",        label: "Total Records"  },
];

// ── Date helpers ───────────────────────────────────────────────────
const parseDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
    if (typeof v === "number") { const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d; }
    if (typeof v !== "string") return null;
    let s = v.trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        const [yy, mm, dd] = s.split("-").map(Number);
        const d = new Date(yy, mm - 1, dd, 0, 0, 0, 0);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) s = s.replace(" ", "T");
    if (s.includes("T") && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = `${s}Z`;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
};

const getLastConvDateValue = (row) =>
    row?.LastConvDate || row?.LastConvDateTime || row?.LastConvDateUtc ||
    row?.LastConvDateString || row?.lastConvDate || row?.lastconvdate;

const formatDate = (v) => {
    const d = parseDate(v);
    if (!d) return "";
    return `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}/${d.getFullYear()}`;
};

// CSV cell escaping — wrap in quotes if it contains comma, quote, or newline.
const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// ── Component ──────────────────────────────────────────────────────
const Transfers = ({ onSearch }) => {
    const today          = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const [startDate,    setStartDate]    = useState(firstDayOfMonth);
    const [endDate,      setEndDate]      = useState(today);
    const [tabValue,     setTabValue]     = useState(TRANSFER_TABS.TEXTS);
    const [rows,         setRows]         = useState([]);
    const [loadingData,  setLoadingData]  = useState(false);
    const [error,        setError]        = useState("");
    const [emailRows,    setEmailRows]    = useState([]);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailError,   setEmailError]   = useState("");

    const buildBoundaries = () => {
        const hasStart = startDate && !Number.isNaN(startDate.getTime());
        const hasEnd   = endDate   && !Number.isNaN(endDate.getTime());
        return {
            startBoundary: hasStart ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0) : null,
            endBoundary:   hasEnd   ? new Date(endDate.getFullYear(),   endDate.getMonth(),   endDate.getDate(),   23, 59, 59, 999) : null,
        };
    };

    const applyClientFilter = (all) => {
        const { startBoundary, endBoundary } = buildBoundaries();
        return (all || []).filter((row) => {
            const d = parseDate(getLastConvDateValue(row));
            if (!d) return false;
            if (startBoundary && d < startBoundary) return false;
            if (endBoundary   && d > endBoundary)   return false;
            return true;
        });
    };

    const loadTextData = async () => {
        setError(""); setLoadingData(true);
        try {
            setRows(applyClientFilter(await fetchTransfersText()));
        } catch {
            setError("Failed to load transfer texts"); setRows([]);
        } finally {
            setLoadingData(false);
        }
    };

    const loadEmailData = async () => {
        setEmailError(""); setEmailLoading(true);
        try {
            setEmailRows(applyClientFilter(await fetchTransfersEmail()));
        } catch {
            setEmailError("Failed to load transfer emails"); setEmailRows([]);
        } finally {
            setEmailLoading(false);
        }
    };

    useEffect(() => {
        tabValue === TRANSFER_TABS.TEXTS ? loadTextData() : loadEmailData();
    }, [tabValue]);

    const handleSearch = async () => {
        if (typeof onSearch === "function") {
            onSearch({
                startDate: startDate?.toISOString() ?? null,
                endDate:   endDate?.toISOString()   ?? null,
            });
        }
        tabValue === TRANSFER_TABS.TEXTS ? await loadTextData() : await loadEmailData();
    };

    const isLoading = tabValue === TRANSFER_TABS.TEXTS ? loadingData : emailLoading;
    const activeRows = tabValue === TRANSFER_TABS.TEXTS ? rows : emailRows;

    // ── CSV export ────────────────────────────────────────────────
    // Exports the active tab's filtered dataset.
    const exportDisabled = isLoading || activeRows.length === 0;

    const exportCsv = () => {
        if (!activeRows.length) return;

        const headers = EXPORT_COLS.map(c => csvEscape(c.label));
        const rowsCsv = activeRows.map(row =>
            EXPORT_COLS.map(col =>
                col.isDate
                    ? csvEscape(formatDate(getLastConvDateValue(row)))
                    : csvEscape(row[col.key])
            ).join(",")
        );
        // BOM + CRLF so Excel opens it cleanly across locales.
        const csv  = "\uFEFF" + [headers.join(","), ...rowsCsv].join("\r\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);

        const now = new Date();
        const ts  = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}`;
        const channel = tabValue === TRANSFER_TABS.TEXTS ? "texts" : "emails";

        const a = document.createElement("a");
        a.href     = url;
        a.download = `transfers_${channel}_${ts}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{ p: 0 }}>
            {/* ── Filters ── */}
            <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 2,
                alignItems: "flex-end",
                pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
            }}>
                <DatePickerComponent
                    label="Start Date"
                    date={startDate}
                    onDateChange={setStartDate}
                    marginRight={0}
                />
                <DatePickerComponent
                    label="End Date"
                    date={endDate}
                    onDateChange={d => { if (d && (!startDate || d >= startDate)) setEndDate(d); }}
                    marginRight={0}
                />
                <Box
                    onClick={handleSearch}
                    sx={{
                        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                        cursor: "pointer", userSelect: "none",
                        background: BLUE, color: "#fff", fontWeight: 500,
                        alignSelf: "flex-end", mb: "1px",
                        "&:hover": { background: "#2a6db5" },
                    }}
                >
                    Search
                </Box>

                {/* Export CSV */}
                <Box
                    onClick={exportDisabled ? undefined : exportCsv}
                    title={exportDisabled ? "No data to export" : "Export current view to CSV"}
                    sx={{
                        px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
                        userSelect: "none",
                        border: "0.5px solid",
                        borderColor: exportDisabled ? "divider" : BLUE,
                        background: "transparent",
                        color: exportDisabled ? "text.disabled" : BLUE,
                        fontWeight: 500,
                        cursor: exportDisabled ? "default" : "pointer",
                        opacity: exportDisabled ? 0.5 : 1,
                        alignSelf: "flex-end", mb: "1px",
                        display: "inline-flex", alignItems: "center", gap: "6px",
                        transition: "all .15s",
                        "&:hover": exportDisabled ? {} : { background: "rgba(55,138,221,0.06)" },
                    }}
                >
                    <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
                    Export CSV
                </Box>

                {/* Channel toggle */}
                <Box sx={{
                    display: "flex", ml: "auto",
                    border: "0.5px solid", borderColor: "divider",
                    borderRadius: "20px", overflow: "hidden",
                    alignSelf: "flex-end",
                }}>
                    {[TRANSFER_TABS.TEXTS, TRANSFER_TABS.EMAILS].map(ch => (
                        <Box key={ch} onClick={() => setTabValue(ch)} sx={{
                            px: "14px", py: "5px", fontSize: 13,
                            fontWeight:  tabValue === ch ? 500 : 400,
                            cursor: "pointer", userSelect: "none",
                            background:  tabValue === ch ? BLUE : "transparent",
                            color:       tabValue === ch ? "#fff" : "text.secondary",
                            transition:  "all .15s",
                        }}>
                            {ch}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* ── Chart ── */}
            {!isLoading && (
                <TransfersLanguageStackedAreaChart
                    rows={tabValue === TRANSFER_TABS.TEXTS ? rows : emailRows}
                    title="Transfers"
                />
            )}

            {/* ── Table ── */}
            {tabValue === TRANSFER_TABS.TEXTS && (
                <TextTransfer rows={rows} loadingData={loadingData} error={error} />
            )}
            {tabValue === TRANSFER_TABS.EMAILS && (
                <EmailTransfer rows={emailRows} loadingData={emailLoading} error={emailError} />
            )}
        </Box>
    );
};

export default Transfers;